import Stack from "@mui/material/Stack";
import MainCustomContainer from "../Containers/MainCustomContainer";
import Typography from "@mui/material/Typography";
import ScrollableTabsButtonAuto from "../Tabs";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { ExamContainer } from "./ExamContainer";
import { MeasureContainer } from "./MeasureContainer";
import { DIALOGS } from "../Dialogs/dialogReducer";
import { useSelector } from "react-redux";
import { RootState } from "../..";
import RenameExamDialog from "../ExamOverview/Dialogs/RenameExamDialog";
import DiscardChangesExamDialog from "./Dialogs/DiscardChangesExamDialog";
import SelectObjectivesExamDialog from "./Dialogs/SelectObjectivesExamDialog";
import SelectExercisesExamDialog from "./Dialogs/SelectExercisesExamDialog";
import PrintExamDialog from "./Dialogs/PrintExamDialog";
import cloneDeep from "lodash/cloneDeep";
import { Box, IconButton, Tooltip } from "@mui/material";
import { COLORS } from "../../constants";
import CustomSnackbar from "../Snackbar/Snackbar";
import { PDFContainer } from "./PDFContainer";
import {
  calculateTotalPoints,
  createPages,
  getImageUrls,
  measureExercisesContainerHight,
  setIsMeasuringContainer,
  setIsMeasuringExercises,
} from "./Functions/FunctionsCreatePages";
import NoObjectivesSelectedDialog from "./Dialogs/NoObjectivesSelectedDialog";
import CreatePdfDialog from "./Dialogs/CreatePdfDialog";
import LoupeIcon from "@mui/icons-material/Loupe";
import * as FunctionsMain from "./Functions/FunctionsMain";
import {
  checkObjectivesSelected,
  closeSelectObjectivesDialog,
  createTabs,
  handleConfirmNoObjectiveSelected,
} from "./Functions/FunctionsChangeObjectives";
import { createPagesPrint, createPdf, createPdfSolutions, setIsCreatingPDF } from "./Functions/FunctionsPrint";
import { ZoomSlider } from "./ZoomSlider";
import { useLocation, useNavigate } from "react-router-dom";
import { DEMO_EXAM_DATA } from "./DemoData";
import { DemoSticker, TopTitleBox } from "./Styles";
import ChangeLayoutDialog from "./Dialogs/ChangeLayoutDialog";

export interface ExerciseHeight {
  id: string;
  height: number;
}

export const ExamBuilder: React.FC = () => {
  const { pathname } = useLocation();
  const isDemoMode = pathname.startsWith("/demo");

  const exerciseRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const examHeaderRef = useRef<HTMLDivElement | null>(null);
  const totalPointsRef = useRef<HTMLDivElement | null>(null);
  const printPageRefs = useRef<HTMLDivElement[]>([]);

  const examAccess = useSelector((state: RootState) => state.examOverview.examAccess);

  const navigate = useNavigate();

  const [groupColorDark, setGroupColorDark] = useState(COLORS.DIDACTO_DARK_GREY);
  const [groupColorLight, setGroupColorLight] = useState(COLORS.DIDACTO_DARK_GREY);

  //Imports from dialogs reducer
  const renameExamDialogOpen = useSelector((state: RootState) => state.dialogs.renameExamDialog);
  const discardChangesExamDialog = useSelector((state: RootState) => state.dialogs.discardChangesExamDialog);
  const selectTopicsExamDialog = useSelector((state: RootState) => state.dialogs.selectTopicsExamDialog);
  const selectExercisesExamDialog = useSelector((state: RootState) => state.dialogs.selectExercisesExamDialog);
  const printExamDialogOpen = useSelector((state: RootState) => state.dialogs.printExamDialog);
  const noObjectivesSelectedDialogOpen = useSelector((state: RootState) => state.dialogs.noObjectiveSelectedDialog);
  const createPdfDialogOpen = useSelector((state: RootState) => state.dialogs.createPdfDialog);
  const changeLayoutDialogOpen = useSelector((state: RootState) => state.dialogs.changeLayoutDialog);

  //Imports from main reducer
  const selectedObjectiveId = useSelector((state: RootState) => state.examBuilder.main.selectedObjectiveId);
  const examData = useSelector((state: RootState) => state.examBuilder.main.examData);
  const pages = useSelector((state: RootState) => state.examBuilder.main.pages);
  const totalPoints = useSelector((state: RootState) => state.examBuilder.main.totalPoints);
  const pagesCreated = useSelector((state: RootState) => state.examBuilder.main.pagesCreated);
  const imageUrls = useSelector((state: RootState) => state.examBuilder.main.imageUrls || {});
  const solutionImageUrls = useSelector((state: RootState) => state.examBuilder.main.solutionImageUrls || {});
  const activeTab = useSelector((state: RootState) => state.examBuilder.main.activeTab);
  const isMeasuringContainer = useSelector((state: RootState) => state.examBuilder.main.isMeasuringContainer);
  const firstPageLoad = useSelector((state: RootState) => state.examBuilder.main.firstPageLoad);
  const openSnackbarSave = useSelector((state: RootState) => state.examBuilder.main.openSnackbarSave);
  const openSnackbarSaved = useSelector((state: RootState) => state.examBuilder.main.openSnackbarSaved);
  const exerciseHeights = useSelector((state: RootState) => state.examBuilder.main.exerciseHeights);
  const initialExamData = useSelector((state: RootState) => state.examBuilder.main.initialData);
  const scrollHeight = useSelector((state: RootState) => state.examBuilder.main.scrollHeight);

  //Imports from create pages reducer
  const isMeasuringExercises = useSelector((state: RootState) => state.examBuilder.createPages.isMeasuringExercises);
  const containerHeight = useSelector((state: RootState) => state.examBuilder.createPages.containerHeight);
  const headerHeight = useSelector((state: RootState) => state.examBuilder.createPages.headerHeight);
  const totalPointsHeigth = useSelector((state: RootState) => state.examBuilder.createPages.totalPointsHeight);
  const selectedLayout = useSelector((state: RootState) => state.examBuilder.createPages.layoutType);

  //Imports from print reducer
  const isCreatingAllPages = useSelector((state: RootState) => state.examBuilder.print.isCreatingAllPages);
  const allPages = useSelector((state: RootState) => state.examBuilder.print.allPages);
  const allTotalPoints = useSelector((state: RootState) => state.examBuilder.print.allTotalPoints);
  const allObjectives = useSelector((state: RootState) => state.examBuilder.print.allObjectives);
  const isCreatingPdf = useSelector((state: RootState) => state.examBuilder.print.isCreatingPdf);
  const isCreatingPdfSolutions = useSelector((state: RootState) => state.examBuilder.print.isCreatingPdfSolutions);
  const currentObejectiveIndex = useSelector((state: RootState) => state.examBuilder.print.currentObjectiveIndex);
  const printSolutions = useSelector((state: RootState) => state.examBuilder.print.printSolutions);
  const createPageNumbers = useSelector((state: RootState) => state.examBuilder.print.createPageNumbers);

  //Imports from change objectives reducer
  const tabs = useSelector((state: RootState) => state.examBuilder.manipulateObjectives.mappedTabs);

  const selectedObjective = useMemo(() => {
    return selectedObjectiveId ? examData.objectives[selectedObjectiveId] : null;
  }, [examData, selectedObjectiveId]);

  const sortedObjectives = useMemo(() => {
    const objectivesArray = Object.values(examData.objectives);
    return FunctionsMain.sortObjectives(objectivesArray);
  }, [examData]);

  const sortedExercises = useMemo(() => {
    return selectedObjective?.exercises
      ? Object.values(selectedObjective.exercises).sort((a, b) => a.index - b.index)
      : [];
  }, [selectedObjective]);

  useEffect(() => {
    if (isDemoMode) {
      FunctionsMain.setExamData(DEMO_EXAM_DATA, DEMO_EXAM_DATA);
    } else {
      FunctionsMain.checkValidLicense(examAccess, examData.grade, navigate);
      FunctionsMain.setInitialExamData(examData);
    }
    FunctionsMain.setFirstPageLoad(true);
    checkObjectivesSelected(sortedObjectives);
    setIsCreatingPDF(false);
  }, []);

  //Maps tabs and sets the first objective for the first page load
  useEffect(() => {
    if (examData && sortedObjectives.length > 0) {
      if (firstPageLoad) {
        createTabs(sortedObjectives);
        FunctionsMain.setHasUnsavedChanges(false);
        FunctionsMain.setInitialExamData(examData);
        FunctionsMain.resetDataPages();
        FunctionsMain.setFirstPageLoad(false);
        FunctionsMain.setOpenSnackbarSave(false);
        FunctionsMain.setOpenSnackbarSaved(false);
      }
    } else {
      FunctionsMain.setMappedTabs(null);
    }
  }, [examData, firstPageLoad]);

  //Checks if changes to the examData were made
  useEffect(() => {
    FunctionsMain.checkChanges(initialExamData, examData);
  }, [initialExamData, examData]);

  //Copies the exercises so it can be restored if the user discard changes
  useEffect(() => {
    FunctionsMain.setExercisesCopy(cloneDeep(sortedExercises));
  }, [sortedExercises]);

  //Measures the height of the container
  useLayoutEffect(() => {
    if (
      containerRef.current &&
      examHeaderRef.current &&
      isMeasuringContainer &&
      !isMeasuringExercises &&
      !firstPageLoad
    ) {
      measureExercisesContainerHight(containerRef, examHeaderRef, totalPointsRef);
      setIsMeasuringContainer(false);
      setIsMeasuringExercises(true);
    }
  }, [containerRef.current, firstPageLoad, isMeasuringContainer]);

  //Measures the heights of the exercises
  useEffect(() => {
    if (sortedExercises.length > 0 && isMeasuringExercises && !firstPageLoad && !isMeasuringContainer) {
      FunctionsMain.setPages([sortedExercises]);
      setIsMeasuringExercises(false);
    }
  }, [imageUrls, sortedExercises, firstPageLoad, isMeasuringExercises, isMeasuringContainer]);

  //Creates the pages
  useEffect(() => {
    if (
      sortedExercises &&
      sortedExercises.every((exercise) => exerciseHeights.hasOwnProperty(exercise.id)) &&
      !isMeasuringContainer &&
      !firstPageLoad
    ) {
      calculateTotalPoints(sortedExercises);
      createPages(sortedExercises, containerHeight, exerciseHeights, headerHeight, totalPointsHeigth);
    }
  }, [exerciseHeights, isMeasuringContainer, firstPageLoad, sortedExercises]);

  useEffect(() => {
    getImageUrls(sortedExercises, imageUrls, solutionImageUrls, isDemoMode);
    calculateTotalPoints(sortedExercises);
  }, [sortedExercises]);

  useEffect(() => {
    if (!pagesCreated) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [pagesCreated]);

  useEffect(() => {
    if (selectExercisesExamDialog) {
      FunctionsMain.setScrollHeight(window.scrollY);
    } else {
      if (pagesCreated) {
        window.scroll(0, scrollHeight);
        FunctionsMain.setScrollHeight(0);
      }
    }
  }, [selectExercisesExamDialog, pagesCreated]);

  //Hooks to create the PDF's
  useEffect(() => {
    if (selectedObjectiveId && isCreatingAllPages && pagesCreated) {
      printPageRefs.current = [];
      createPagesPrint(
        sortedObjectives,
        selectedObjectiveId,
        currentObejectiveIndex,
        allPages,
        pages,
        allTotalPoints,
        totalPoints,
      );
    }
  }, [pagesCreated, isCreatingAllPages, selectedObjectiveId]);

  useEffect(() => {
    if (selectedObjectiveId && isCreatingPdf) {
      createPdf(examData.name, sortedObjectives[0].id, printSolutions, selectedObjectiveId);
    }
  }, [isCreatingPdf]);

  useEffect(() => {
    if (selectedObjectiveId && isCreatingPdfSolutions) {
      createPdfSolutions(examData.name, sortedObjectives[0].id, selectedObjectiveId);
    }
  }, [isCreatingPdfSolutions]);

  //Effect to create the group colors
  useEffect(() => {
    let groupId = selectedObjectiveId?.toString()[1];
    if (groupId === "1") {
      setGroupColorDark(COLORS.ALGEBRA_DARK);
      setGroupColorLight(COLORS.ALGEBRA_LIGHT);
    }
    if (groupId === "2") {
      setGroupColorDark(COLORS.GEOMETRIE_DARK);
      setGroupColorLight(COLORS.GEOMETRIE_LIGHT);
    }
    if (groupId === "3") {
      setGroupColorDark(COLORS.APPLIED_MATHS_DARK);
      setGroupColorLight(COLORS.APPLIED_MATHS_LIGHT);
    }
  }, [selectedObjectiveId]);

  return (
    <>
      <MainCustomContainer>
        <Stack style={{ position: "relative" }}>
          <TopTitleBox>
            <Typography variant="h2">Prüfungsgenerator</Typography>
            {isDemoMode && <DemoSticker>DEMO</DemoSticker>}
          </TopTitleBox>

          <Box onClick={() => FunctionsMain.handleOpenDialog(DIALOGS.RENAME_EXAM_DIALOG)} sx={{ cursor: "pointer" }}>
            <Typography variant="h3">{examData.name}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "end" }}>
            {tabs && <ScrollableTabsButtonAuto tabs={tabs} activeTab={activeTab} />}
            <Tooltip title={"Kompetenzen hinzufügen"}>
              <IconButton
                size="small"
                onClick={() => FunctionsMain.handleOpenDialog(DIALOGS.SELECT_TOPICS_EXAM_DIALOG)}
              >
                <LoupeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {(() => {
            switch (true) {
              case sortedObjectives.length > 0 && !isCreatingPdf:
                return selectedObjective && pages && sortedExercises && examData ? (
                  <div>
                    {pagesCreated
                      ? pages.map((pageExercises, pageIndex) => (
                          <ExamContainer
                            key={pageIndex}
                            pageIndex={pageIndex}
                            selectedObjective={selectedObjective}
                            pageExercises={pageExercises}
                            exerciseRefs={exerciseRefs}
                            pages={pages}
                            examName={examData.name}
                            amountExercises={sortedExercises.length}
                            totalPoints={totalPoints}
                            imageUrls={imageUrls}
                            solutionImageUrls={solutionImageUrls}
                            isCreatingAllPages={isCreatingAllPages}
                            sortedExercises={sortedExercises}
                            selectedLayout={selectedLayout}
                            groupColorDark={groupColorDark}
                            groupColorLight={groupColorLight}
                          />
                        ))
                      : pages.map((pageExercises, pageIndex) => (
                          <Box key={pageIndex}>
                            <Stack
                              sx={{
                                display: isCreatingAllPages ? "none" : "flex",
                                alignItems: "center",
                                marginTop: "24px",
                                gap: "24px",
                                width: "70vw",
                              }}
                            >
                              <Typography>Prüfung wird erstellt...</Typography>
                              <CircularProgress />
                            </Stack>
                            <MeasureContainer
                              pageIndex={pageIndex}
                              selectedObjective={selectedObjective}
                              pageExercises={pageExercises}
                              exerciseRefs={exerciseRefs}
                              pages={pages}
                              examName={examData.name}
                              isMeasuringContainer={isMeasuringContainer}
                              containerRef={containerRef}
                              examHeaderRef={examHeaderRef}
                              totalPointsRef={totalPointsRef}
                              imageUrls={imageUrls}
                            />
                          </Box>
                        ))}
                  </div>
                ) : null;

              case isCreatingPdf:
                return (
                  <div id="exam-pdf" style={{ position: "absolute", top: "0", left: "0", visibility: "visible" }}>
                    {allObjectives.map((objective, objectiveIndex) =>
                      allPages[objectiveIndex].map((pageExercises, pageIndex) => {
                        const runningIndex =
                          allPages.slice(0, objectiveIndex).reduce((n, p) => n + p.length, 0) + pageIndex;
                        return (
                          <PDFContainer
                            key={`${objective.id}-${pageIndex}`}
                            pageIndex={pageIndex}
                            selectedObjective={objective}
                            pages={allPages[objectiveIndex]}
                            pageExercises={pageExercises}
                            examName={examData.name}
                            totalPoints={allTotalPoints[objectiveIndex]}
                            imageUrls={imageUrls}
                            solutionImageUrls={solutionImageUrls}
                            pageNumber={runningIndex + 1}
                            createPageNumbers={createPageNumbers}
                            selectedLayout={selectedLayout}
                          />
                        );
                      }),
                    )}
                  </div>
                );

              default:
                return null;
            }
          })()}
        </Stack>
        <ZoomSlider />
      </MainCustomContainer>

      {renameExamDialogOpen && (
        <RenameExamDialog
          open={renameExamDialogOpen}
          renameExam={FunctionsMain.renameExam}
          handleCancel={() => FunctionsMain.handleCloseDialog(DIALOGS.RENAME_EXAM_DIALOG)}
          currentName={examData.name}
        />
      )}

      {discardChangesExamDialog && (
        <DiscardChangesExamDialog
          open={discardChangesExamDialog}
          handleCancel={() => FunctionsMain.handleCloseDialog(DIALOGS.DISCARD_CHANGES_EXAM_DIALOG)}
        />
      )}

      {selectTopicsExamDialog && (
        <SelectObjectivesExamDialog
          open={selectTopicsExamDialog}
          handleCancel={() => closeSelectObjectivesDialog(sortedObjectives)}
          grade={examData.grade}
        />
      )}

      {selectExercisesExamDialog && (
        <SelectExercisesExamDialog
          open={selectExercisesExamDialog}
          objectiveId={selectedObjectiveId}
          grade={examData.grade}
          objective={selectedObjective}
          sortedExercises={sortedExercises}
          handleCancel={() => FunctionsMain.handleCloseDialog(DIALOGS.SELECT_EXERCISES_EXAM_DIALOG)}
        />
      )}

      {printExamDialogOpen && (
        <PrintExamDialog
          open={printExamDialogOpen}
          sortedObjectives={sortedObjectives}
          handleCancel={() => FunctionsMain.handleCloseDialog(DIALOGS.PRINT_EXAM_DIALOG)}
        />
      )}

      {changeLayoutDialogOpen && (
        <ChangeLayoutDialog
          open={changeLayoutDialogOpen}
          handleCancel={() => FunctionsMain.handleCloseDialog(DIALOGS.CHANGE_LAYOUT_DIALOG)}
        />
      )}

      {noObjectivesSelectedDialogOpen && (
        <NoObjectivesSelectedDialog
          open={noObjectivesSelectedDialogOpen}
          handleConfirm={() => handleConfirmNoObjectiveSelected()}
        />
      )}
      {createPdfDialogOpen && <CreatePdfDialog open={createPdfDialogOpen} />}

      {openSnackbarSave && (
        <CustomSnackbar
          open={openSnackbarSave}
          onClose={FunctionsMain.handleCloseSnackbarSave}
          message="speichert..."
          autoHideDuration={null}
          color={"gray"}
        />
      )}

      {openSnackbarSaved && (
        <CustomSnackbar
          open={openSnackbarSaved}
          onClose={FunctionsMain.handleCloseSnackbarSaved}
          message="Gespeichert"
          autoHideDuration={3000}
          color={COLORS.DIDACTO_BLUE}
        />
      )}
    </>
  );
};

export default ExamBuilder;
